import React, { useState } from "react";
import { sendPasswordResetEmail } from "firebase/auth";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import auth from "../firebase.init";
import { Helmet } from "react-helmet";

const ResetPassword = () => {
    const [email, setEmail] = useState("");
    const [resetSent, setResetSent] = useState(false);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const handleResetPassword = async (e) => {
        e.preventDefault();
        
        if (!email.trim()) {
            setError("Please enter your email address");
            return;
        }
        
        setLoading(true);
        try {
            await sendPasswordResetEmail(auth, email);
            setResetSent(true);
            setError("");
        } catch (error) {
            setError(getErrorMessage(error));
        } finally {
            setLoading(false);
        }
    };

    const getErrorMessage = (error) => {
        if (!error) return "";
        switch (error.code) {
            case "auth/user-not-found":
                return "No user found with this email address.";
            case "auth/invalid-email":
                return "Please enter a valid email address.";
            default:
                return "An unexpected error occurred. Please try again later.";
        }
    };

    return (
        <>
            <Helmet>
                <title>Reset Password - Recover Your Account</title>
                <meta name="description" content="Forgot your password? Reset it here and regain access to your account. Follow the instructions to securely reset your password." />
            </Helmet>

            <style jsx>{`
                .reset-container {
                    min-height: 100vh;
                    background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
                    display: flex;
                    align-items: center;
                    padding: 2rem 0;
                    position: relative;
                    overflow: hidden;
                }
                
                .reset-container::before {
                    content: "";
                    position: absolute;
                    top: -50px;
                    right: -50px;
                    width: 300px;
                    height: 300px;
                    border-radius: 50%;
                    background: rgba(255, 255, 255, 0.1);
                    z-index: 0;
                }
                
                .reset-container::after {
                    content: "";
                    position: absolute;
                    bottom: -100px;
                    left: -100px;
                    width: 400px;
                    height: 400px;
                    border-radius: 50%;
                    background: rgba(255, 255, 255, 0.05);
                    z-index: 0;
                }
                
                .reset-card {
                    border-radius: 15px;
                    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
                    border: none;
                    backdrop-filter: blur(10px);
                    background-color: rgba(255, 255, 255, 0.9);
                    z-index: 1;
                }
                
                .reset-card .card-body {
                    padding: 2.5rem;
                }
                
                .form-control:focus {
                    border-color: #86b7fe;
                    box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
                }
                
                .btn-primary {
                    background-color: #4361ee;
                    border-color: #4361ee;
                    border-radius: 8px;
                    padding: 0.75rem 1.5rem;
                    font-weight: 600;
                    transition: all 0.3s ease;
                }
                
                .btn-primary:hover {
                    background-color: #3a56d4;
                    border-color: #3a56d4;
                    transform: translateY(-2px);
                }
                
                .btn-link {
                    color: #4361ee;
                    text-decoration: none;
                    font-weight: 600;
                }
                
                .btn-link:hover {
                    color: #3a56d4;
                    text-decoration: underline;
                }
                
                .success-icon {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    width: 100px;
                    height: 100px;
                    border-radius: 50%;
                    background-color: rgba(25, 135, 84, 0.1);
                    margin: 0 auto 1.5rem;
                }
                
                .alert-danger {
                    border-radius: 8px;
                    border-left: 4px solid #dc3545;
                }
                
                .logo-img {
                    height: 50px;
                }
                
                @media (max-width: 767px) {
                    .reset-card {
                        margin: 0 1rem;
                    }
                    .reset-card .card-body {
                        padding: 1.5rem;
                    }
                }
            `}</style>

            <div className="reset-container">
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-md-6 col-lg-5">
                            <motion.div 
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5 }}
                                className="card reset-card"
                            >
                                <div className="card-body">
                                    <div className="text-center mb-4">
                                      
                                        <h2 className="fw-bold">Reset Password</h2>
                                        <p className="text-muted">Enter your email to receive reset instructions</p>
                                    </div>

                                    {resetSent ? (
                                        <motion.div 
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            className="text-center py-3"
                                        >
                                            <div className="success-icon">
                                                <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '3rem' }}></i>
                                            </div>
                                            <h4 className="mb-3">Email Sent Successfully!</h4>
                                            <p className="mb-3">We've sent password reset instructions to <strong>{email}</strong></p>
                                            <p className="text-muted small">
                                                Didn't receive the email? Check your spam folder or{" "}
                                                <button 
                                                    onClick={() => {
                                                        setResetSent(false);
                                                        handleResetPassword();
                                                    }}
                                                    className="btn btn-link p-0"
                                                >
                                                    try again
                                                </button>
                                            </p>
                                            <Link to="/login" className="btn btn-primary mt-3">
                                                Return to Login
                                            </Link>
                                        </motion.div>
                                    ) : (
                                        <form onSubmit={handleResetPassword}>
                                            {error && (
                                                <div className="alert alert-danger d-flex align-items-center mb-4" role="alert">
                                                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                                                    <div>{error}</div>
                                                </div>
                                            )}

                                            <div className="mb-4">
                                                <label htmlFor="email" className="form-label fw-medium">Email Address</label>
                                                <div className="input-group">
                                                    <span className="input-group-text bg-light border-end-0">
                                                        <i className="bi bi-envelope"></i>
                                                    </span>
                                                    <input 
                                                        type="email" 
                                                        className="form-control border-start-0 ps-0"
                                                        id="email"
                                                        value={email}
                                                        onChange={(e) => setEmail(e.target.value)}
                                                        placeholder="Enter your email"
                                                        required
                                                    />
                                                </div>
                                            </div>

                                            <div className="d-grid mb-4">
                                                <button 
                                                    type="submit" 
                                                    className="btn btn-primary"
                                                    disabled={loading}
                                                >
                                                    {loading ? (
                                                        <>
                                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                            Sending...
                                                        </>
                                                    ) : (
                                                        'Send Reset Instructions'
                                                    )}
                                                </button>
                                            </div>

                                            <div className="text-center">
                                                <p className="mb-0">
                                                    Remember your password? <Link to="/login" className="btn-link">Back to Login</Link>
                                                </p>
                                            </div>
                                        </form>
                                    )}
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ResetPassword;